import type { ReportFilterSelectOption } from '../shared/report-filter-select-option.model';
import {
    REPORT_RECORD_COUNT_ALLOWED_NUMBERS,
    REPORT_RECORD_COUNT_ELEMENT_ID,
} from '../agreements/report-record-count.constants';
import {
    DWT_PAYLOAD_REPORT_STATUS_CODES,
    DWT_PAYLOAD_STATUS_CODE_FAILED,
    DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL,
    DWT_PAYLOAD_STATUS_CODE_WARNINGS,
    type DwtPayloadReportStatusCode,
    isDwtPayloadReportStatusCode,
} from '../dwt-payloads/dwt-payloads.constants';

/** Get-RegulatoryReports — DWT submissions Trace sent to DEFRA. */
export const DWT_DEFRA_SUBMISSIONS_ACTIVITY = 'Get-RegulatoryReports' as const;

/** Excel structured table name for DEFRA submission field rows. */
export const DWT_DEFRA_SUBMISSIONS_TABLE_NAME = 'TraceDwtSubmissionsToDefra';

/** Task pane facility multiselect; options come from Get-UserInfo. */
export const DWT_DEFRA_SUBMISSIONS_FACILITY_CODE_ELEMENT_ID = 'filter-dwt-defra-facility-code';

export const DWT_DEFRA_SUBMISSIONS_STATUS_ELEMENT_ID = 'filter-dwt-defra-status';

export const DWT_DEFRA_SUBMISSIONS_START_DATE_ELEMENT_ID = 'filter-dwt-defra-start-date';

export const DWT_DEFRA_SUBMISSIONS_END_DATE_ELEMENT_ID = 'filter-dwt-defra-end-date';

export const DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ELEMENT_ID = REPORT_RECORD_COUNT_ELEMENT_ID;

/** Inclusive last-N-day window used when start/end are left blank. */
export const DWT_DEFRA_SUBMISSIONS_DEFAULT_LOOKBACK_DAYS = 7;

export const DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_DEFAULT_NUM = 5_000;

export const DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_DEFAULT_VALUE = '5000';

export const DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ALLOWED_NUMBERS: readonly number[] =
    REPORT_RECORD_COUNT_ALLOWED_NUMBERS;

export const DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_SELECT_OPTIONS: readonly ReportFilterSelectOption[] =
    DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ALLOWED_NUMBERS.map((n) => ({
        value: String(n),
        label: String(n),
    }));

/** Sheet labels for the submission as a whole (same value on every field row). */
export const DWT_DEFRA_SUBMISSION_STATUS_SUCCESS = 'Success' as const;
export const DWT_DEFRA_SUBMISSION_STATUS_WARNING = 'Warning' as const;
export const DWT_DEFRA_SUBMISSION_STATUS_ERROR = 'Error' as const;

export type DwtDefraSubmissionStatusLabel =
    | typeof DWT_DEFRA_SUBMISSION_STATUS_SUCCESS
    | typeof DWT_DEFRA_SUBMISSION_STATUS_WARNING
    | typeof DWT_DEFRA_SUBMISSION_STATUS_ERROR;

/** BE `RegulatorySubmissionStatus`: 2 Success, 3 Retrying (Warning), 4 Failed. */
export const DWT_DEFRA_SUBMISSION_STATUS_CODES: readonly DwtPayloadReportStatusCode[] =
    DWT_PAYLOAD_REPORT_STATUS_CODES;

export const DWT_DEFRA_SUBMISSION_STATUS_CODE_TO_SHEET_LABEL: Record<
    DwtPayloadReportStatusCode,
    DwtDefraSubmissionStatusLabel
> = {
    [DWT_PAYLOAD_STATUS_CODE_SUCCESSFUL]: DWT_DEFRA_SUBMISSION_STATUS_SUCCESS,
    [DWT_PAYLOAD_STATUS_CODE_WARNINGS]: DWT_DEFRA_SUBMISSION_STATUS_WARNING,
    [DWT_PAYLOAD_STATUS_CODE_FAILED]: DWT_DEFRA_SUBMISSION_STATUS_ERROR,
};

export const DWT_DEFRA_SUBMISSION_STATUS_CODE_VALUES: readonly string[] =
    DWT_DEFRA_SUBMISSION_STATUS_CODES.map((code) => String(code));

export function resolveDwtDefraSubmissionsRecordCount(value: number | undefined): number {
    if (
        typeof value === 'number' &&
        DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_ALLOWED_NUMBERS.includes(value)
    ) {
        return value;
    }
    return DWT_DEFRA_SUBMISSIONS_RECORD_COUNT_DEFAULT_NUM;
}

export function dwtDefraSubmissionStatusCodesFromMultiselectValues(selected: string[]): number[] {
    const codes = selected
        .map((value) => Number(value.trim()))
        .filter((n): n is DwtPayloadReportStatusCode => isDwtPayloadReportStatusCode(n));
    return Array.from(new Set(codes));
}
